import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import api, { fmtIDR } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useI18n } from "../lib/i18n";
import { Search } from "lucide-react";
import { toast } from "sonner";
import ExtraFilters from "../components/ExtraFilters";
import MonogramTile from "../components/MonogramTile";
import GameStrip from "../components/GameStrip";

const EMPTY = { game_id: "", category_slug: "", search: "", in_stock: true, server_id: "", region: "", faction: "", min_price: "", max_price: "" };

export default function Marketplace() {
  const { t } = useI18n();
  const { money, altMoney } = useCurrency();
  const [products, setProducts] = useState([]);
  const [games, setGames] = useState([]);
  const [cats, setCats] = useState([]);
  const [servers, setServers] = useState([]);
  const [filters, setFilters] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = async (f = filters) => {
    if (f.min_price !== "" && f.max_price !== "" && Number(f.min_price) > Number(f.max_price)) {
      toast.error(t("market.err_minmax"));
      return;
    }
    setLoading(true);
    const params = {};
    ["game_id", "category_slug", "search", "server_id", "region", "faction", "min_price", "max_price"].forEach((k) => {
      if (f[k] !== "") params[k] = f[k];
    });
    if (f.in_stock) params.in_stock = true;
    try {
      const r = await api.get("/products", { params });
      setProducts(r.data);
    } catch {
      toast.error(t("market.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([api.get("/games"), api.get("/categories"), api.get("/servers")]).then(([g, c, s]) => {
      setGames(g.data); setCats(c.data); setServers(s.data);
    });
  }, []);

  useEffect(() => { load(); }, [filters.game_id, filters.category_slug, filters.in_stock, filters.server_id, filters.region, filters.faction]);

  const reset = () => { setFilters(EMPTY); load(EMPTY); };

  return (
    <div className="min-h-screen bg-[#101114]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-2">MARKETPLACE</div>
            <h1 className="text-3xl lg:text-4xl font-black text-[#F5F3ED]" style={{fontFamily:"'Playfair Display', serif"}}>{t("nav.marketplace")}</h1>
          </div>
        </div>

        {/* Filters */}
        <GameStrip games={games} selected={filters.game_id} onSelect={(id) => setFilters({ ...filters, game_id: id, server_id: "", region: "", faction: "" })} />

        <div className="bg-[#202127] border border-[#35353B] rounded-lg p-4 mb-8 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A8AD]" size={16} />
            <input data-testid="market-search-input" placeholder={t("market.search")} value={filters.search}
              onChange={(e)=>setFilters({...filters, search: e.target.value})}
              onKeyDown={(e)=>e.key==='Enter' && load()}
              className="bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] focus:border-[#D4AF37] rounded-sm pl-9 pr-3 py-2 w-full outline-none" />
          </div>
          <select data-testid="market-game-filter" value={filters.game_id} onChange={(e)=>setFilters({...filters, game_id: e.target.value, server_id: "", region: "", faction: ""})}
            className="bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] rounded-sm px-3 py-2 outline-none">
            <option value="">{t("market.all_games")}</option>
            {games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select data-testid="market-category-filter" value={filters.category_slug} onChange={(e)=>setFilters({...filters, category_slug: e.target.value})}
            className="bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] rounded-sm px-3 py-2 outline-none">
            <option value="">{t("market.all_categories")}</option>
            {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-[#F5F3ED] px-3">
            <input data-testid="market-instock-checkbox" type="checkbox" checked={filters.in_stock} onChange={(e)=>setFilters({...filters, in_stock: e.target.checked})}
              className="accent-[#D4AF37]" />
            {t("market.in_stock")}
          </label>
          <ExtraFilters filters={filters} setFilters={setFilters} servers={servers} onApply={() => load()} onReset={reset} />
        </div>

        {loading ? <div className="text-center py-20 text-[#A8A8AD]">{t("common.loading")}</div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map(p => {
              const game = games.find(g => g.id === p.game_id);
              const server = servers.find(s => s.id === p.server_id);
              return (
                <Link key={p.id} to={`/product/${p.id}`} data-testid={`product-card-${p.id}`} className="bg-[#202127] border border-[#35353B] rounded-lg overflow-hidden hover:-translate-y-1 hover:border-[#D4AF37] transition-all duration-300 group">
                  <MonogramTile name={game?.name || p.name} label={p.category_slug} className="h-40 border-b border-[#35353B]" testId={`product-img-${p.id}`} />
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#A8A8AD] mb-1">{game?.name || "-"}{server ? ` · ${server.name}` : ""}{server?.region ? ` · ${server.region}` : ""}{server?.faction ? ` · ${server.faction}` : ""}</div>
                    <div className="text-sm font-semibold text-[#F5F3ED] mb-3 line-clamp-2 min-h-[40px]">{p.name}</div>
                    <div className="flex items-center justify-between">
                      <div className="text-lg font-bold text-[#D4AF37]">{money(p.sell_price_idr)}</div>
                      <div data-testid={`product-alt-price-${p.id}`} className="text-xs text-[#A8A8AD]">{altMoney(p.sell_price_idr)}</div>
                      <div className="text-xs text-[#A8A8AD]">{t("market.stock")}: {p.stock_available}</div>
                    </div>
                  </div>
                </Link>
              );
            })}
            {products.length === 0 && <div className="col-span-full text-center py-20 text-[#A8A8AD]">{t("common.empty")}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
